import { FeatureGateBannerQuotaLevel } from "@/components/banners/feature-gate-banner/feature-gate-banner.types";
import {
  MDSIcon,
  MDSIconKind,
  MDSPlanCard,
  MDSPlanCardBackground,
  MDSPlanCardBorder,
  MDSPlanCardSlot,
  MDSPlanId,
} from "@/mds";
import {
  MDSButton,
  MDSButtonVariant,
  MDSBadge,
  MDSBadgeVariant,
  MDSDialogHeaderContent,
  MDSDialogSlotName,
  MDSDialogTextContent,
  MDSModalDialog,
  MDSText,
  MDSTextVariant,
} from "@/mds";
import {
  freeFeatures,
  proFeatures,
} from "@/pages/settings/components/subscription-management/data";
import { GatedFeature, PlanId } from "@/store/feature-gates/types";
import { useAppStore } from "@/store/utils/hooks";
import { useEffect, useState } from "react";
import { trackEvent } from "@/domains/metrics/segment";
import { TrackedEvent, PaywallContext } from "@/domains/metrics/tracked-event";
import { buildMonetizationUsagePayload } from "@/domains/metrics/monetization";
import { BillingStrategyEnum } from "@/store/pages/SettingsSubscriptionManagementPageStore/types";
import { useLocation } from "react-router-dom";
import styled from "@emotion/styled";
import { subscriptionModule } from "@/modules/subscriptions";

interface UpsellModalProps {
  isOpen: boolean;
  onClose: () => void;
  gatedFeature?: GatedFeature;
  level: FeatureGateBannerQuotaLevel;
}

interface UpsellModalConfig {
  title: string;
}

const getUpsellModalConfig = (
  level: FeatureGateBannerQuotaLevel,
  feature?: GatedFeature
): UpsellModalConfig => {
  switch (feature) {
    case GatedFeature.NoteCreate:
      return {
        title:
          level === FeatureGateBannerQuotaLevel.Exceeded
            ? "You've reached your monthly note limit"
            : "You're nearing your monthly note limit",
      };
    case GatedFeature.ChatMessageSend:
      return {
        title:
          level === FeatureGateBannerQuotaLevel.Exceeded
            ? "You've reached your monthly chat limit"
            : "You're nearing your monthly chat limit",
      };
    case GatedFeature.DarkMode:
      return {
        title: "Upgrade to Mem Pro for Dark mode",
      };
    default:
      return {
        title:
          level === FeatureGateBannerQuotaLevel.Exceeded
            ? "You've reached your monthly usage limit"
            : "You're nearing your monthly usage limit",
      };
  }
};

export const UpsellModal = ({ isOpen, onClose, gatedFeature, level }: UpsellModalProps) => {
  const { title } = getUpsellModalConfig(level, gatedFeature);
  const { store } = useAppStore();
  const subscriptionStore = store.subscriptions;
  const context: PaywallContext | undefined = subscriptionStore.upsellModalContext;
  const [isRedirecting, setIsRedirecting] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (!isOpen) return;

    const trackIt = async () => {
      const monetizationProperties = await buildMonetizationUsagePayload(store);
      trackEvent(TrackedEvent.PaywallView, {
        context,
        ...monetizationProperties,
      });
    };

    trackIt();
  }, [context, gatedFeature, isOpen, store]);

  const handleUpgrade = async () => {
    if (isRedirecting) return;
    setIsRedirecting(true);
    const monetizationProperties = await buildMonetizationUsagePayload(store);
    trackEvent(TrackedEvent.PaywallUpgradeClick, { context, ...monetizationProperties });

    try {
      await subscriptionModule.createCheckoutSessionAndNavigate({
        billingStrategy: BillingStrategyEnum.MONTHLY,
        location,
        store,
      });
    } finally {
      setIsRedirecting(false);
      onClose();
    }
  };

  const handleClose = () => {
    trackEvent(TrackedEvent.PaywallCancelClick, { context });
    onClose();
  };

  const mapPlanIdToMDSPlanId = (planId: PlanId): MDSPlanId => {
    switch (planId) {
      case PlanId.Paid:
        return MDSPlanId.Paid;
      default:
        return MDSPlanId.Free;
    }
  };

  return (
    <MDSModalDialog isOpen={isOpen} handleClose={handleClose}>
      <MDSDialogHeaderContent slot={MDSDialogSlotName.Header}>
        <MDSText variant={MDSTextVariant.HeaderL}>{title}</MDSText>
      </MDSDialogHeaderContent>

      <MDSDialogTextContent slot={MDSDialogSlotName.Content}>
        <MDSText variant={MDSTextVariant.ParagraphSm}>
          Upgrade to Pro for unlimited notes and chat messages.
        </MDSText>
        <CardsContainer>
          {/* Free plan card */}
          <div className="freeCard">
            <MDSPlanCard
              plan={mapPlanIdToMDSPlanId(PlanId.Free)}
              features={freeFeatures}
              background={MDSPlanCardBackground.Default}
              border={MDSPlanCardBorder.Default}
            >
              <MDSText variant={MDSTextVariant.HeaderL} slot={MDSPlanCardSlot.Title}>
                Free
              </MDSText>
              <MDSBadge
                label="Current"
                variant={MDSBadgeVariant.Default}
                slot={MDSPlanCardSlot.TitleBadge}
              />

              <MDSText variant={MDSTextVariant.HeaderL} slot={MDSPlanCardSlot.Price}>
                $0 forever
              </MDSText>
            </MDSPlanCard>
          </div>

          {/* Pro plan card */}
          <MDSPlanCard
            plan={mapPlanIdToMDSPlanId(PlanId.Paid)}
            features={proFeatures}
            background={MDSPlanCardBackground.Emphasis}
            border={MDSPlanCardBorder.Emphasis}
          >
            <MDSText variant={MDSTextVariant.HeaderL} slot={MDSPlanCardSlot.Title}>
              Pro
            </MDSText>
            <MDSBadge
              label="Unlimited"
              variant={MDSBadgeVariant.MoreEmphasis}
              slot={MDSPlanCardSlot.TitleBadge}
            />
            <div slot={MDSPlanCardSlot.Price}>$12 / month</div>
          </MDSPlanCard>
        </CardsContainer>
      </MDSDialogTextContent>

      <FooterFlex slot={MDSDialogSlotName.Footer}>
        <MDSButton
          variant={MDSButtonVariant.Emphasis}
          onClick={handleUpgrade}
          isLoading={isRedirecting}
        >
          <MDSIcon kind={MDSIconKind.StarSolid} /> Upgrade to Pro
        </MDSButton>
      </FooterFlex>
    </MDSModalDialog>
  );
};

const FooterFlex = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing[1]};
`;

const CardsContainer = styled.div({
  display: "flex",
  gap: "12px",
  flexWrap: "nowrap",
  containerType: "inline-size",
  containerName: "upsellCards",
  "@container upsellCards (max-width: 580px)": {
    ".freeCard": {
      display: "none",
    },
  },
});
